/**
 * MCP Server Card Component
 * Displays individual MCP server info with status and actions
 */

import React, { useState } from 'react';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Server, TestTube, Pencil, Trash2, AlertCircle, CheckCircle, Circle, Clock, RefreshCw, Activity } from 'lucide-react';
import type { MCPServer } from '@/types/system';
import type { MCPConnectionStatus } from '@/lib/api/tools';
import { useTestMCPConnection } from '../hooks/useSystemConfig';
import { useI18n } from '@/i18n';

interface MCPServerCardProps {
  server: MCPServer;
  connectionStatus?: MCPConnectionStatus;
  onEdit: (server: MCPServer) => void;
  onDelete: (serverId: string) => void;
}

export const MCPServerCard: React.FC<MCPServerCardProps> = ({ server, connectionStatus, onEdit, onDelete }) => {
  const { messages } = useI18n();
  const t = messages.systemSettings.mcp?.serverCard;
  const [testResult, setTestResult] = useState<{ success: boolean; error?: string } | null>(null);
  const testConnection = useTestMCPConnection();

  const handleTest = async () => {
    setTestResult(null);
    try {
      const result = await testConnection.mutateAsync(server.id);
      setTestResult(result);
    } catch (error) {
      setTestResult({ success: false, error: String(error) });
    }
  };

  // Format uptime for display
  const formatUptime = (seconds: number | null): string => {
    if (seconds === null) return '-';
    if (seconds < 60) return `${Math.round(seconds)}s`;
    if (seconds < 3600) return `${Math.round(seconds / 60)}m`;
    return `${Math.round(seconds / 3600)}h ${Math.round((seconds % 3600) / 60)}m`;
  };

  // Get status from connectionStatus or fallback to server.status
  const effectiveState = connectionStatus?.state || (server.status === 'connected' ? 'connected' : server.status === 'error' ? 'failed' : 'disconnected');

  const getStatusIcon = () => {
    switch (effectiveState) {
      case 'connected':
        return <CheckCircle className="w-4 h-4 text-green-600" />;
      case 'connecting':
      case 'reconnecting':
        return <RefreshCw className="w-4 h-4 text-yellow-600 animate-spin" />;
      case 'failed':
        return <AlertCircle className="w-4 h-4 text-red-600" />;
      default:
        return <Circle className="w-4 h-4 text-gray-400" />;
    }
  };

  const getStatusColor = () => {
    switch (effectiveState) {
      case 'connected':
        return 'bg-green-50 border-green-200';
      case 'connecting':
      case 'reconnecting':
        return 'bg-yellow-50 border-yellow-200';
      case 'failed':
        return 'bg-red-50 border-red-200';
      default:
        return 'bg-gray-50 border-gray-200';
    }
  };

  const getStateLabel = () => {
    const states = t?.states;
    switch (effectiveState) {
      case 'connected':
        return states?.connected || 'Connected';
      case 'connecting':
        return states?.connecting || 'Connecting...';
      case 'reconnecting':
        return states?.reconnecting || 'Reconnecting...';
      case 'failed':
        return states?.failed || 'Failed';
      case 'disconnected':
        return states?.disconnected || 'Disconnected';
      default:
        return states?.unknown || 'Unknown';
    }
  };

  const getHealthBadge = () => {
    if (!connectionStatus?.health) return null;
    const colors: Record<string, string> = {
      healthy: 'bg-green-100 text-green-700',
      unhealthy: 'bg-red-100 text-red-700',
      unknown: 'bg-gray-100 text-gray-700',
    };
    return (
      <span className={`px-2 py-0.5 text-xs rounded ${colors[connectionStatus.health] || colors.unknown}`}>
        {connectionStatus.health}
      </span>
    );
  };

  return (
    <Card className={`p-4 ${getStatusColor()} transition-colors`}>
      <div className="flex items-start justify-between">
        <div className="flex items-start gap-3 flex-1">
          <Server className="w-5 h-5 text-gray-600 mt-1" />
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <h4 className="font-medium text-gray-900">{server.name}</h4>
              {getStatusIcon()}
              {server.config.status === 0 && (
                <span className="px-2 py-0.5 text-xs bg-gray-200 text-gray-700 rounded">
                  {t?.disabled || 'Disabled'}
                </span>
              )}
            </div>

            {server.config.description && (
              <p className="text-sm text-gray-600 mb-2">{server.config.description}</p>
            )}

            <div className="space-y-1 text-xs text-gray-500">
              <div className="flex items-center gap-2">
                <span className="font-medium">{t?.transport || 'Transport'}:</span>
                <span className="px-2 py-0.5 bg-white rounded border border-gray-200">
                  {server.config.transport}
                </span>
              </div>

              {server.config.transport === 'stdio' && server.config.command && (
                <div className="flex items-start gap-2">
                  <span className="font-medium">{t?.command || 'Command'}:</span>
                  <code className="px-2 py-0.5 bg-white rounded border border-gray-200 font-mono">
                    {server.config.command}
                    {server.config.args && ` ${server.config.args.join(' ')}`}
                  </code>
                </div>
              )}

              {(server.config.transport === 'sse' || server.config.transport === 'streamable-http') && server.config.url && (
                <div className="flex items-center gap-2">
                  <span className="font-medium">{t?.url || 'URL'}:</span>
                  <code className="px-2 py-0.5 bg-white rounded border border-gray-200 font-mono text-xs break-all">
                    {server.config.url}
                  </code>
                </div>
              )}

              {server.tools_count !== undefined && (
                <div className="flex items-center gap-2">
                  <span className="font-medium">{t?.tools || 'Tools'}:</span>
                  <span className="px-2 py-0.5 bg-white rounded border border-gray-200">
                    {server.tools_count}
                  </span>
                </div>
              )}

              {/* Enhanced Status Display */}
              <div className="flex items-center gap-2 mt-2 flex-wrap">
                <span className={`px-2 py-0.5 text-xs rounded font-medium ${
                  effectiveState === 'connected' ? 'bg-green-100 text-green-700' :
                  effectiveState === 'reconnecting' || effectiveState === 'connecting' ? 'bg-yellow-100 text-yellow-700' :
                  effectiveState === 'failed' ? 'bg-red-100 text-red-700' :
                  'bg-gray-100 text-gray-700'
                }`}>
                  {getStateLabel()}
                </span>
                {getHealthBadge()}
                {connectionStatus?.uptime_seconds != null && connectionStatus.uptime_seconds > 0 && (
                  <span className="flex items-center gap-1 text-xs text-gray-500">
                    <Clock className="w-3 h-3" />
                    {formatUptime(connectionStatus.uptime_seconds)}
                  </span>
                )}
                {connectionStatus?.tool_count != null && connectionStatus.tool_count > 0 && (
                  <span className="flex items-center gap-1 text-xs text-gray-500">
                    <Activity className="w-3 h-3" />
                    {connectionStatus.tool_count} tools
                  </span>
                )}
              </div>

              {/* Retry Progress */}
              {connectionStatus && (effectiveState === 'reconnecting' || effectiveState === 'connecting') && (
                <div className="mt-2 p-2 bg-yellow-50 rounded border border-yellow-200 text-xs">
                  <div className="flex items-center justify-between text-yellow-700">
                    <span>{(t?.retryProgress || 'Retry {current} / {max}').replace('{current}', String(connectionStatus.retry_count)).replace('{max}', String(connectionStatus.max_retries))}</span>
                  </div>
                  <div className="mt-1 h-1.5 bg-yellow-200 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-yellow-500 transition-all duration-300"
                      style={{ width: `${(connectionStatus.retry_count / connectionStatus.max_retries) * 100}%` }}
                    />
                  </div>
                </div>
              )}

              {/* Error Display */}
              {connectionStatus?.last_error && (
                <div className="mt-2 p-2 bg-red-50 rounded border border-red-200 text-xs">
                  <div className="text-red-700 font-medium">{t?.lastError || 'Last Error'}:</div>
                  <div className="text-red-600 truncate" title={connectionStatus.last_error}>
                    {connectionStatus.last_error}
                  </div>
                </div>
              )}
            </div>

            {testResult && (
              <div
                className={`mt-3 p-2 rounded text-xs ${
                  testResult.success
                    ? 'bg-green-100 text-green-800 border border-green-200'
                    : 'bg-red-100 text-red-800 border border-red-200'
                }`}
              >
                {testResult.success ? (
                  <div className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4" />
                    <span>{t?.connectionTestSuccess || 'Connection test successful'}</span>
                  </div>
                ) : (
                  <div className="flex items-start gap-2">
                    <AlertCircle className="w-4 h-4 mt-0.5" />
                    <span>{testResult.error || t?.connectionTestFailed || 'Connection test failed'}</span>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        <div className="flex items-center gap-1 ml-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={handleTest}
            disabled={testConnection.isPending}
            title={t?.testConnection || 'Test connection'}
          >
            <TestTube className="w-4 h-4" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onEdit(server)}
            title={t?.editServer || 'Edit server'}
          >
            <Pencil className="w-4 h-4" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              const confirmMsg = (t?.confirmDelete || 'Delete MCP server "{name}"?').replace('{name}', server.name);
              if (confirm(confirmMsg)) {
                onDelete(server.id);
              }
            }}
            title={t?.deleteServer || 'Delete server'}
          >
            <Trash2 className="w-4 h-4 text-red-600" />
          </Button>
        </div>
      </div>
    </Card>
  );
};
